/**
 * ADR-0231 §2 — signed crawl reports: wire constants shared by the
 * template generator (Worker JS / plugin PHP) and the origin verifier.
 * The HMAC itself is computed in the Worker (WebCrypto), PHP
 * (`hash_hmac`) and Node (`node:crypto`); this module only fixes the
 * format so the three cannot drift.
 *
 * Headers on `POST /v1/public/crawl-event`:
 *   X-Sill-Install    inst_<ULID>
 *   X-Sill-Key        ikey_<ULID>
 *   X-Sill-Timestamp  unix seconds (integer)
 *   X-Sill-Nonce      16 random bytes, base64url (22 chars)
 *   X-Sill-Signature  v1=<base64url(HMAC-SHA256(secret, string_to_sign))>
 *
 *   string_to_sign = "sill-report-v1\n" + install_id + "\n" + key_id + "\n"
 *                  + timestamp + "\n" + nonce + "\n" + hex(sha256(raw body))
 *
 * The secret is the base64url TEXT as embedded in the template (the HMAC
 * key is its UTF-8 bytes — identical in JS, PHP and Node).
 */
export const REPORT_SIGNATURE_VERSION = 'v1';
export const REPORT_SIGNING_PREFIX = 'sill-report-v1';
export const REPORT_HEADER_INSTALL = 'x-sill-install';
export const REPORT_HEADER_KEY = 'x-sill-key';
export const REPORT_HEADER_TIMESTAMP = 'x-sill-timestamp';
export const REPORT_HEADER_NONCE = 'x-sill-nonce';
export const REPORT_HEADER_SIGNATURE = 'x-sill-signature';
export const REPORT_HEADERS = [
    REPORT_HEADER_INSTALL,
    REPORT_HEADER_KEY,
    REPORT_HEADER_TIMESTAMP,
    REPORT_HEADER_NONCE,
    REPORT_HEADER_SIGNATURE,
];
/** Allowed clock skew either side of the origin clock. */
export const REPORT_MAX_SKEW_SECONDS = 300;
const ULID = '[0-9A-HJKMNP-TV-Z]{26}';
export const INSTALL_ID_RE = new RegExp(`^inst_${ULID}$`);
export const INSTALL_KEY_ID_RE = new RegExp(`^ikey_${ULID}$`);
export const REPORT_NONCE_RE = /^[A-Za-z0-9_-]{22}$/;
export const REPORT_TIMESTAMP_RE = /^[0-9]{9,11}$/;
export const REPORT_SIGNATURE_RE = /^v1=[A-Za-z0-9_-]{43}$/;
export function reportStringToSign(install_id, key_id, timestamp, nonce, body_sha256_hex) {
    return `${REPORT_SIGNING_PREFIX}\n${install_id}\n${key_id}\n${timestamp}\n${nonce}\n${body_sha256_hex}`;
}
/**
 * ADR-0239 — one install template version for Worker + plugin. Bumping
 * this constant bumps the response `X-Sill-Install` header, the outbound
 * User-Agent strings in both templates, the plugin header + readme
 * Stable tag, and the comparison target the dashboard / scanner / admin
 * fleet-redeploy use to decide whether a site is up to date.
 */
export const INSTALL_TEMPLATE_VERSION = '1.29';
/**
 * ADR-0245 §4.4.1 S2-enforce follow-up (2026-10-18 incident) — the
 * install template version that first supports the blk/v2 / ppath/v2
 * hash-then-sign envelopes. Installs below this version cannot verify
 * v2 and must still receive v1 envelopes.
 *
 * The publisher reads this constant: if the site's observed
 * `install_version` is `>= V2_INSTALL_MIN_VERSION` → publish v2;
 * otherwise fall back to v1 (truncated when the full canonical form
 * would exceed the KMS 4096-byte cap).
 */
export const V2_INSTALL_MIN_VERSION = '1.11';
/**
 * ADR-0250 — minimum install template version that reads the ppath/v3
 * envelope (which carries the Tier A built-in catalog signed-in-band
 * so a catalog change ships on an API deploy, not a Worker/plugin
 * redeploy). Installs below this version keep receiving v2 envelopes
 * and apply their baked-in Tier A list.
 */
export const V3_INSTALL_MIN_VERSION = '1.13';
/** Worker template version (alias of {@link INSTALL_TEMPLATE_VERSION}). */
export const WORKER_TEMPLATE_VERSION = INSTALL_TEMPLATE_VERSION;
/** WordPress plugin version (alias of {@link INSTALL_TEMPLATE_VERSION}). */
export const WORDPRESS_PLUGIN_VERSION = INSTALL_TEMPLATE_VERSION;
/** Response-header value: `worker/<version>` or `wp/<version>`. */
export function installTemplateHeaderValue(kind) {
    return `${kind}/${INSTALL_TEMPLATE_VERSION}`;
}
/**
 * `X-Sill-Install` response header, served by the Worker + plugin on the
 * well-known routes they own. SEPARATE from the request-direction
 * `X-Sill-Install` the trust-report verifier reads on
 * `POST /v1/public/crawl-event`: the two header surfaces are disjoint by
 * direction and by shape (`inst_<ULID>` vs `worker|wp/<version>`).
 */
export const INSTALL_RESPONSE_HEADER = 'X-Sill-Install';
/**
 * Parse a Worker / plugin install response header value. Returns `null`
 * on absent, malformed, or unknown-kind values; otherwise the pair
 * `{kind, version}`. The scanner uses the parsed version to populate
 * `site_install_version.install_version`.
 */
export function parseInstallTemplateHeader(raw) {
    if (typeof raw !== 'string')
        return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0 || trimmed.length > 64)
        return null;
    const match = /^(worker|wp)\/([A-Za-z0-9][A-Za-z0-9._-]{0,31})$/.exec(trimmed);
    if (match === null)
        return null;
    const kind = match[1];
    // match[1] / match[2] are regex-guaranteed to be defined.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return { kind, version: match[2] };
}
/**
 * Lexicographic + numeric-aware comparison for install template versions
 * of the shape `MAJOR.MINOR[.PATCH]`. Returns -1 / 0 / 1 like
 * `Array.sort` compareFn. Unknown shapes compare equal (ambiguous).
 */
export function compareInstallTemplateVersions(a, b) {
    const parse = (v) => {
        if (!/^[0-9]+(\.[0-9]+){0,3}$/.test(v))
            return null;
        return v.split('.').map((s) => Number(s));
    };
    const ap = parse(a);
    const bp = parse(b);
    if (ap === null || bp === null)
        return 0;
    const len = Math.max(ap.length, bp.length);
    for (let i = 0; i < len; i++) {
        const av = ap[i] ?? 0;
        const bv = bp[i] ?? 0;
        if (av < bv)
            return -1;
        if (av > bv)
            return 1;
    }
    return 0;
}
/**
 * The last install version that changed the Cloudflare Worker template.
 * Plugin-only releases bump {@link INSTALL_TEMPLATE_VERSION} (one shared
 * header / UA version) without changing the Worker, so a Worker at this
 * version or newer is up to date: asking a merchant to redeploy it would
 * change nothing (founder 2026-10-27, vbout.com flagged on 1.20 while
 * 1.21-1.26 were plugin-only). Bump this whenever
 * `buildCloudflareWorkerScript` changes; a template-hash test enforces it.
 *
 * 1.27 (2026-10-28, richardfallah.com) — the /llms.txt + /agent
 * origin-first branches collapse a same-host canonical-trailing-slash
 * 3xx (e.g. WP 301 `/llms.txt` → `/llms.txt/`) to "merchant has no
 * file" and fall through to Sill, instead of returning the 301.
 */
export const WORKER_TEMPLATE_CHANGED_IN = '1.27';
/** The version an install of this kind should be on to be up to date. */
export function latestInstallVersionFor(kind) {
    return kind === 'worker' ? WORKER_TEMPLATE_CHANGED_IN : INSTALL_TEMPLATE_VERSION;
}
/**
 * True when `observed` is strictly older than the latest version for
 * `kind` ({@link latestInstallVersionFor}). `null` observed (header absent
 * on a known install) counts as outdated — the header is only present
 * on 1.5+.
 */
export function isInstallVersionOutdated(observed, kind) {
    if (observed === null || observed === undefined || observed.length === 0)
        return true;
    return compareInstallTemplateVersions(observed, latestInstallVersionFor(kind)) < 0;
}
/**
 * ADR-0258 — WordPress plugin update checks. The plugin's own
 * `pre_set_site_transient_update_plugins` hook calls
 * `GET /v1/wp-plugin/update-check` with install HMAC credentials; this
 * block fixes the shared signing scheme so the plugin, PHP tests, and
 * the Node verifier cannot drift.
 *
 * Headers (reuse the ADR-0231 install credential surface, plus a GET-
 * only method + path binding that is independent of the request body):
 *   X-Sill-Install    inst_<ULID>
 *   X-Sill-Key        ikey_<ULID>
 *   X-Sill-Timestamp  unix seconds (integer)
 *   X-Sill-Nonce      22-char base64url
 *   X-Sill-Signature  v1=<base64url(HMAC-SHA256(secret, string_to_sign))>
 *
 *   string_to_sign = "sill-wp-plugin-update-v1\n" + method + "\n" + path + "\n"
 *                  + install_id + "\n" + key_id + "\n" + timestamp + "\n" + nonce
 *
 * The purpose prefix is distinct from `sill-report-v1`, so a captured
 * crawl-event signature can never be replayed on this surface and vice
 * versa.
 */
export const PLUGIN_UPDATE_SIGNING_PREFIX = 'sill-wp-plugin-update-v1';
export function pluginUpdateStringToSign(method, path, install_id, key_id, timestamp, nonce) {
    return `${PLUGIN_UPDATE_SIGNING_PREFIX}\n${method}\n${path}\n${install_id}\n${key_id}\n${timestamp}\n${nonce}`;
}
//# sourceMappingURL=report-signing.js.map